/**
 * Template AST and fill schemas.
 *
 * This is the contract between the editor (produces templates), the Worker
 * (validates and stores them), the doc-engine (assembles them) and the PDF
 * renderer (consumes the assembled output). Every one of those imports these
 * schemas, so they cannot drift apart. See PLAN.md 3.6, 3.6a and 3.16.
 *
 * Structural rules live here (zod). Cross-references — does this placeholder
 * point at a real field, is a party field used outside a party block — live in
 * doc-engine's validateTemplate, because they need the whole tree.
 */
import { z } from 'zod';
import { LocationRef } from './locations';
import { IdPrefill } from './id-prefill';
import { LINE_SPACINGS, SPACE_STEPS_PT } from './styles';

/* ------------------------------------------------------------------ ids */

/** Internal field id. The admin-facing label ("S/O", "Name") is stored separately. */
export const FIELD_ID_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
export const FieldId = z.string().regex(FIELD_ID_PATTERN, 'field id must be lowercase snake_case');

/** Id of any structural node: sentence, block, clause entry, option, row, cell. */
export const NodeId = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, 'invalid node id');

/* ------------------------------------------------------------- parties */

/**
 * Repeating sections. Sellers and buyers are parties; `property` is the
 * schedule of properties (one or several, each of its own type). All three
 * repeat the same way: one copy of the block's wording per item.
 */
export const PARTY_SIDES = ['seller', 'buyer', 'property'] as const;
export const PartySide = z.enum(PARTY_SIDES);
export type PartySide = z.infer<typeof PartySide>;

/** Built-in types of a seller or buyer. Admins may add their own (Template.partyTypes). */
export const PARTY_TYPES = ['public', 'firm_company', 'bank'] as const;
/** Built-in property types. Admins may add their own too. */
export const PROPERTY_TYPES = ['agricultural_land', 'vacant_site', 'individual_house', 'residential_apartment'] as const;

export const PARTY_TYPE_ID_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
/** A built-in or admin-defined type id (public, firm_company, trust, vacant_site, ...). */
export const PartyType = z.string().regex(PARTY_TYPE_ID_PATTERN, 'type id must be lowercase snake_case');
export type PartyType = z.infer<typeof PartyType>;

/** person / organisation drives the PAN holder check; property types have neither. */
export const TypeKind = z.enum(['person', 'organisation', 'property']);
export type TypeKind = z.infer<typeof TypeKind>;

export const CustomType = z.strictObject({
  id: PartyType,
  label: z.string().trim().min(1).max(60),
  kind: TypeKind,
});
export type CustomType = z.infer<typeof CustomType>;

/* ---------------------------------------------------------------- units */

// Guntas and ankanams (Andhra Pradesh) come last, so existing lists and hints keep their order.
export const AREA_UNITS = ['acres', 'cents', 'hectares', 'sq_yd', 'sq_m', 'sq_ft', 'guntas', 'ankanams'] as const;
export const AreaUnit = z.enum(AREA_UNITS);
export type AreaUnit = z.infer<typeof AreaUnit>;

/* ------------------------------------------------------------- formulas */

export type Comparison = '>' | '>=' | '<' | '<=' | '=';

/**
 * A calculation, built by the admin from lists (no typed code):
 *   num     a fixed number, e.g. "0.05"
 *   field   another field's value (an area can be read in a chosen unit)
 *   sum     the total of a field over every seller / buyer / property
 *   op      two calculations joined by + - * /
 *   if      "if A > B then X otherwise Y" (slabs, e.g. stamp duty)
 */
export type Expr =
  | { num: string }
  | { field: string; unit?: AreaUnit }
  | { sum: string; side: PartySide; unit?: AreaUnit }
  | { op: '+' | '-' | '*' | '/'; a: Expr; b: Expr }
  | { if: { a: Expr; cmp: Comparison; b: Expr }; then: Expr; else: Expr };

const NUMBER_TEXT = /^-?\d{1,15}(\.\d{1,12})?$/;

export const Expr: z.ZodType<Expr> = z.lazy(() => z.union([
  z.strictObject({ num: z.string().regex(NUMBER_TEXT, 'must be a number') }),
  z.strictObject({ field: FieldId, unit: AreaUnit.optional() }),
  z.strictObject({ sum: FieldId, side: PartySide, unit: AreaUnit.optional() }),
  z.strictObject({ op: z.enum(['+', '-', '*', '/']), a: Expr, b: Expr }),
  z.strictObject({
    if: z.strictObject({ a: Expr, cmp: z.enum(['>', '>=', '<', '<=', '=']), b: Expr }),
    then: Expr,
    else: Expr,
  }),
]) as z.ZodType<Expr>);

/* -------------------------------------------------------------- fields */

export const FIELD_TYPES = ['text', 'number', 'currency', 'date', 'enum', 'area', 'calculated', 'location', 'list', 'aadhaar', 'pan', 'tan'] as const;
export const FieldType = z.enum(FIELD_TYPES);
export type FieldType = z.infer<typeof FieldType>;

export const FieldDef = z.strictObject({
  id: FieldId,
  /** Exactly what the admin typed, e.g. "S/O". Drives the form label "S/O : ....". */
  label: z.string().trim().min(1).max(120),
  type: FieldType,
  /** `party` fields repeat per seller/buyer and may only appear inside a party block. */
  scope: z.enum(['document', 'party']),
  /** A required field that is empty blocks generation. Optional empties drop their sentence. */
  required: z.boolean(),
  /** Only for type `enum`. */
  options: z.array(z.string().trim().min(1).max(80)).min(2).max(20).optional(),
  maxLength: z.int().min(1).max(2000).optional(),
  /** Only for type `area`: units the executive may type in; the first is the default. */
  units: z.array(AreaUnit).min(1).max(AREA_UNITS.length).optional(),
  /** Only for type `calculated`: how the value is worked out. */
  formula: Expr.optional(),
  /** Only for type `calculated`: printed as an amount (Rs.) or a plain number. */
  result: z.enum(['currency', 'number']).optional(),
  /** Only for type `calculated`: decimals kept (rounded half up). */
  decimals: z.int().min(0).max(4).optional(),
  /** Only for type `list` (older templates): a column of an uploaded list. New templates use `location`. */
  list: z.strictObject({ id: z.uuid(), column: z.string().trim().min(1).max(80) }).optional(),
  /** Only for type `location`: which built-in place level this is (District, Mandal, Village…). */
  location: LocationRef.optional(),
  /** Fill this box from an Aadhaar card photo (owner 10-21). Absent = automatic (label / location level). */
  idPrefill: IdPrefill.optional(),
});
export type FieldDef = z.infer<typeof FieldDef>;

/* ---------------------------------------------------------------- runs */

/** Every text mark, in the order editors, importers and renderers apply them (owner 10-21: B, I, U). */
export const MARK_NAMES = ['bold', 'italic', 'underline'] as const;
export type MarkName = (typeof MARK_NAMES)[number];

export const Marks = z.strictObject({
  bold: z.literal(true).optional(),
  italic: z.literal(true).optional(),
  underline: z.literal(true).optional(),
});
export type Marks = z.infer<typeof Marks>;

/** One definition of "same formatting" for the editor, importer and engine (they each had their own). */
export function sameMarks(a: Marks | undefined, b: Marks | undefined): boolean {
  return MARK_NAMES.every(m => !!a?.[m] === !!b?.[m]);
}

export const TextRun = z.strictObject({
  kind: z.literal('text'),
  text: z.string().min(1).max(10_000),
  marks: Marks.optional(),
});

export const PlaceholderRun = z.strictObject({
  kind: z.literal('placeholder'),
  field: FieldId,
  /**
   * `words` renders an amount in Indian-system words. An area unit prints an
   * area field converted to that unit, whatever unit the executive typed.
   */
  format: z.enum(['default', 'words', ...AREA_UNITS]).optional(),
  marks: Marks.optional(),
});

/** SELLER vs SELLERS, sells vs sell — chosen from the party count on `side`. */
export const PluralRun = z.strictObject({
  kind: z.literal('plural'),
  side: PartySide,
  one: z.string().min(1).max(80),
  many: z.string().min(1).max(80),
  marks: Marks.optional(),
});

/** Joins one field across every party on a side: "Sri A, Sri B and Sri C". */
export const PartyRefRun = z.strictObject({
  kind: z.literal('partyRef'),
  side: PartySide,
  /** Which field to use per type, e.g. public -> name, firm_company -> entity_name. */
  fields: z.record(PartyType, FieldId),
  marks: Marks.optional(),
});

export const InlineRun = z.discriminatedUnion('kind', [TextRun, PlaceholderRun, PluralRun, PartyRefRun]);
export type InlineRun = z.infer<typeof InlineRun>;

/**
 * When a sentence or part is kept (PLAN.md 3.6b):
 *   { field, filled: true }  keep only when that field has a value
 *   { field, equals }        keep only when a choice field has that option
 * "Filled" works for Aadhaar/PAN too: the server sees presence, never the value.
 */
export const FilledCondition = z.strictObject({ field: FieldId, filled: z.literal(true) });
export const EqualsCondition = z.strictObject({ field: FieldId, equals: z.string().min(1).max(80) });
export const Condition = z.union([FilledCondition, EqualsCondition]);
export type Condition = z.infer<typeof Condition>;

export function isFilledCondition(c: Condition): c is z.infer<typeof FilledCondition> {
  return 'filled' in c;
}

/**
 * Drops as a unit when any placeholder inside is empty or `when` fails; the
 * surrounding sentence survives. Fragments do not nest.
 */
export const FragmentRun = z.strictObject({
  kind: z.literal('fragment'),
  runs: z.array(InlineRun).min(1).max(200),
  when: Condition.optional(),
});

export const SentenceRun = z.discriminatedUnion('kind', [TextRun, PlaceholderRun, PluralRun, PartyRefRun, FragmentRun]);
export type SentenceRun = z.infer<typeof SentenceRun>;

/** The unit of conditional dropping. Boundaries are explicit, never inferred at render time. */
export const Sentence = z.strictObject({
  id: NodeId,
  runs: z.array(SentenceRun).min(1).max(500),
  when: Condition.optional(),
});
export type Sentence = z.infer<typeof Sentence>;

/* -------------------------------------------------------------- blocks */

/**
 * Line spacing and space before/after a heading or paragraph (owner 10-21), only from the closed lists in
 * styles.ts. Absent = Standard. An empty object is refused so the editor round-trip stays exact.
 */
export const BlockSpacing = z.strictObject({
  line: z.literal([...LINE_SPACINGS]).optional(),
  before: z.literal([...SPACE_STEPS_PT]).optional(),
  after: z.literal([...SPACE_STEPS_PT]).optional(),
}).refine(s => s.line !== undefined || s.before !== undefined || s.after !== undefined, 'spacing needs at least one setting');
export type BlockSpacing = z.infer<typeof BlockSpacing>;

export const HeadingBlock = z.strictObject({
  type: z.literal('heading'),
  id: NodeId,
  level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  sentence: Sentence,
  spacing: BlockSpacing.optional(),
});

export const ParagraphBlock = z.strictObject({
  type: z.literal('paragraph'),
  id: NodeId,
  sentences: z.array(Sentence).min(1).max(200),
  spacing: BlockSpacing.optional(),
});
export type ParagraphBlock = z.infer<typeof ParagraphBlock>;

export const ListBlock = z.strictObject({
  type: z.literal('list'),
  id: NodeId,
  ordered: z.boolean(),
  items: z.array(z.strictObject({ id: NodeId, sentences: z.array(Sentence).min(1).max(50) })).min(1).max(200),
});

export const TableCell = z.strictObject({
  id: NodeId,
  /** May be empty: a deliberately blank cell. */
  sentences: z.array(Sentence).max(50),
});

export const TableRow = z.strictObject({
  id: NodeId,
  cells: z.array(TableCell).min(1).max(8),
  /** Printed once per seller / buyer / property (e.g. one Rule-3 row per property). */
  repeat: PartySide.optional(),
});

export const TableBlock = z.strictObject({
  type: z.literal('table'),
  id: NodeId,
  headerRow: z.boolean(),
  rows: z.array(TableRow).min(1).max(200),
});

/** Blocks allowed inside party variants: no nested parties, no clause sections. */
export const ContentBlock = z.discriminatedUnion('type', [HeadingBlock, ParagraphBlock, ListBlock, TableBlock]);
export type ContentBlock = z.infer<typeof ContentBlock>;

export const ClauseOption = z.strictObject({
  id: NodeId,
  /** Shown to the executive when choosing. executive-view sees this, not the prose. */
  label: z.string().trim().min(1).max(160),
  body: z.array(ParagraphBlock).min(1).max(20),
});
export type ClauseOption = z.infer<typeof ClauseOption>;

/**
 * One numbered slot. Standalone = 1 option, min 1, max 1.
 * Optional = 1 option, min 0, max 1. Either-of = N options, min 1, max 1.
 */
export const ClauseEntry = z.strictObject({
  id: NodeId,
  label: z.string().trim().min(1).max(160),
  min: z.int().min(0).max(10),
  max: z.int().min(1).max(10),
  options: z.array(ClauseOption).min(1).max(10),
});
export type ClauseEntry = z.infer<typeof ClauseEntry>;

export const ClauseSectionBlock = z.strictObject({
  type: z.literal('clauseSection'),
  id: NodeId,
  entries: z.array(ClauseEntry).min(1).max(200),
});

export const PartyBlock = z.strictObject({
  type: z.literal('partyBlock'),
  id: NodeId,
  side: PartySide,
  /** Prefix each party's first paragraph with 1., 2., ... */
  numbered: z.boolean(),
  /** Only one item allowed on this side (e.g. a deed for exactly one property). */
  single: z.literal(true).optional(),
  /** The admin authors real prose per type (built-in or his own) and toggles between them. */
  variants: z.record(PartyType, z.array(ContentBlock).min(1).max(100)),
});
export type PartyBlock = z.infer<typeof PartyBlock>;

export const Block = z.discriminatedUnion('type', [
  HeadingBlock, ParagraphBlock, ListBlock, TableBlock, ClauseSectionBlock, PartyBlock,
]);
export type Block = z.infer<typeof Block>;

export const Template = z.strictObject({
  schemaVersion: z.literal(1),
  regType: z.literal('sale'),
  /** Sale Deed, Sale Agreement With Possession, ... Each is its own template. */
  natureOfDocument: z.string().trim().min(1).max(120),
  title: z.string().trim().min(1).max(200),
  fields: z.array(FieldDef).max(500),
  /** The admin's own seller/buyer types (Trust, HUF…) and property types. */
  partyTypes: z.array(CustomType).max(30).optional(),
  blocks: z.array(Block).min(1).max(2000),
});
export type Template = z.infer<typeof Template>;

/* ---------------------------------------------------------------- fill */

/** Sent by the browser to the Worker in place of any Tier 3 value. Never the value itself. */
export const Presence = z.strictObject({ present: z.literal(true) });
export type Presence = z.infer<typeof Presence>;

export const FillValue = z.union([z.string().max(5000), Presence]);
export type FillValue = z.infer<typeof FillValue>;

export const PartyFill = z.strictObject({
  type: PartyType,
  values: z.record(FieldId, FillValue),
});
export type PartyFill = z.infer<typeof PartyFill>;

export const Fill = z.strictObject({
  values: z.record(FieldId, FillValue),
  parties: z.strictObject({
    seller: z.array(PartyFill).max(50),
    buyer: z.array(PartyFill).max(50),
    /** Absent in fills saved before property blocks existed; read it with itemsOf(). */
    property: z.array(PartyFill).max(50).optional(),
  }),
  /** Clause entry id -> selected option ids. Standalone entries need no entry here. */
  clauses: z.record(NodeId, z.array(NodeId).max(10)),
});
export type Fill = z.infer<typeof Fill>;

/**
 * Stands in for one letter/digit grapheme of redacted body text in the
 * skeleton template sent to executive-view. Spaces and punctuation are kept,
 * so wrapping, spacing and sentence dropping behave exactly like the original.
 * Renderers draw runs of this character as grey bars.
 */
export const REDACTED = '\uE001';

export function emptyFill(): Fill {
  return { values: {}, parties: { seller: [], buyer: [] }, clauses: {} };
}

/** The items on one side; `property` may be missing in older fills. */
export function itemsOf(fill: Fill, side: PartySide): PartyFill[] {
  return fill.parties[side] ?? [];
}

/* --------------------------------------------------------- type labels */

export interface TypeInfo { id: PartyType; label: string; kind: TypeKind; builtIn: boolean }

const BUILT_IN: readonly TypeInfo[] = [
  { id: 'public', label: 'Public', kind: 'person', builtIn: true },
  { id: 'firm_company', label: 'Firm / Company', kind: 'organisation', builtIn: true },
  { id: 'bank', label: 'Bank', kind: 'organisation', builtIn: true },
  { id: 'agricultural_land', label: 'Agricultural land', kind: 'property', builtIn: true },
  { id: 'vacant_site', label: 'Vacant site', kind: 'property', builtIn: true },
  { id: 'individual_house', label: 'Individual house', kind: 'property', builtIn: true },
  { id: 'residential_apartment', label: 'Residential apartment', kind: 'property', builtIn: true },
];

export const SIDE_LABEL: Record<PartySide, { one: string; many: string }> = {
  seller: { one: 'Seller', many: 'Sellers' },
  buyer: { one: 'Buyer', many: 'Buyers' },
  property: { one: 'Property', many: 'Properties' },
};

/** Every type usable on a side: built-in first, then the admin's own, in his order. */
export function typesForSide(template: Pick<Template, 'partyTypes'>, side: PartySide): TypeInfo[] {
  const wantProperty = side === 'property';
  const custom = (template.partyTypes ?? []).map(t => ({ ...t, builtIn: false }));
  return [...BUILT_IN, ...custom].filter(t => (t.kind === 'property') === wantProperty);
}

export function typeInfo(template: Pick<Template, 'partyTypes'>, id: string): TypeInfo | undefined {
  return BUILT_IN.find(t => t.id === id) ?? (template.partyTypes ?? []).map(t => ({ ...t, builtIn: false })).find(t => t.id === id);
}

export function typeLabel(template: Pick<Template, 'partyTypes'>, id: string): string {
  return typeInfo(template, id)?.label ?? id.replace(/_/g, ' ');
}

export function isBuiltInType(id: string): boolean {
  return BUILT_IN.some(t => t.id === id);
}
